'use client';
import {
  SearchAnalyticsBoundary,
  SearchAnalyticsItem,
  type SearchAnalyticsData,
} from '@eliadmin/core/src/components/analytics/search-analytics-boundary';
import { SearchSort } from '@eliadmin/core/src/components/header/search-bar/search-sort';
import { searchSortOptions } from '@eliadmin/core/src/utils/search-sort';

import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { SearchFields } from '@eliadmin/core/src/utils/product-utils';
import {
  buildProductsQuery,
  parseProductSearchParams,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { Suspense, useMemo, useState, useEffect } from 'react';
import { Paragraph } from '../../components/ui/paragraph';
import { ProductCardSkeleton } from '../../components/product/product-card/skeleton';

export default function SearchPage() {
  return (
    <Suspense fallback={<SearchPageSkeleton />}>
      <SearchPageContent />
    </Suspense>
  );
}

function SearchPageContent() {
  const locale = useLocaleStore((s) => s.currentLocale);
  const { t } = useTranslations();
  const { me, isInitialized } = useCustomerStore();

  const searchParams = useSearchParams();

  const parsed = useMemo(() => parseProductSearchParams(searchParams), [searchParams]);

  const [usedCustomSearchFields, setUsedCustomSearchFields] = useState<SearchFields | undefined>(
    parsed.search_fields,
  );

  // Keep local "used" fields in sync with URL
  useEffect(() => {
    setUsedCustomSearchFields(parsed.search_fields);
  }, [parsed.search_fields]);

  // legacy sync function removed; handled directly in onApply

  const query = buildProductsQuery({
    ...parsed,
    search_fields: usedCustomSearchFields ?? parsed.search_fields,
    results: 12,
    ...searchSortOptions(searchParams),
  });

  const productRequestUri = buildProductsRequest(query).uri;
  const productResetUri = buildProductsRequest({ ...query, page: undefined }).uri;

  const {
    data: products,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [CacheKey.PRODUCTS, locale, me?.authToken ?? null, productRequestUri],
    queryFn: () =>
      fetchProducts({
        locale,
        query,
        authToken: me?.authToken,
      }),
    enabled: isInitialized,
    refetchOnWindowFocus: false,
  });

  return (
    <section
      className={cn(
        'custom-section',
        'gap-8 py-8 sm:gap-10 sm:py-12 md:py-16 lg:gap-12 lg:py-[72px]',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-2 sm:gap-3 lg:gap-4',
        )}
      >
        <h3 className="text-[28px] font-medium tracking-tighter sm:text-3xl lg:text-4xl">
          {t('text211')}
        </h3>
        <Paragraph>
          {t('text211')}: {searchParams.get('search') || ''}
        </Paragraph>
      </div>

      <>
        <SearchSort />
        <SearchAnalyticsBoundary
          mode="submitted"
          active={!isFetching}
          data={{
            manifest: products?.meta?.search,
            identity: JSON.stringify([
              locale,
              buildProductsRequest(query).uri,
              me?.customer.id ?? null,
            ]),
            locale,
            results: (products?.data ?? []).map((product) => ({
              type: 'product',
              id: product.id,
              href: product.slug,
            })),
            offset: products?.meta?.from
              ? products.meta.from - 1
              : (Number(query.page ?? 1) - 1) * Number(query.results ?? 12),
          }}
        >
          <div
            className={cn(
              'custom-container',
              'grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 sm:gap-y-8 md:grid-cols-3 lg:grid-cols-4 lg:gap-x-6',
            )}
          >
            {isLoading
              ? [...Array(8)].map((_, index) => <ProductCardSkeleton key={index} />)
              : products?.data?.map((product, searchIndex) => (
                  <SearchAnalyticsItem index={searchIndex} key={searchIndex}>
                    {<ProductCard product={product} key={product.slug} />}
                  </SearchAnalyticsItem>
                ))}
          </div>
        </SearchAnalyticsBoundary>
      </>
      <div className={cn('custom-container', 'flex items-center justify-center')}>
        <Suspense>
          <Pagination links={products?.meta?.links ?? []} resetWhen={[productResetUri]} />
        </Suspense>
      </div>
    </section>
  );
}

function SearchPageSkeleton() {
  return (
    <section
      className={cn(
        'custom-section',
        'gap-8 py-8 sm:gap-10 sm:py-12 md:py-16 lg:gap-12 lg:py-[72px]',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 sm:gap-y-8 md:grid-cols-3 lg:grid-cols-4 lg:gap-x-6',
        )}
      >
        {[...Array(8)].map((_, index) => (
          <ProductCardSkeleton key={index} />
        ))}
      </div>
    </section>
  );
}
